'use client'

import { ReactNode, useCallback, useEffect, useRef } from 'react'
import { createContainer } from 'unstated-next'

import {
  LmfAnalyticsProvider,
  LmfAnalyticsTrackFunction,
} from '@syconium/little-miss-figgy/dist/components/Analytics/LmfAnalyticsProvider'
import { useStableCallback } from '@syconium/little-miss-figgy/dist/lib/hooks/useStableCallback'

import {
  iterableCartItemAddedEventName,
  iterableCartItemRemovedEventName,
  toIterableCartLinePayload,
} from '../../api/dynamic/iterableCart'
import { useConvertCartItemIntoSignalData } from '../../brunswick/containers/cart/utils'
import { useGoogleTagManager } from '../../lib/hooks/useGoogleTagManager/useGoogleTagManager'
import { type Cart } from '../../types/figs'
import { NostoPlacementId } from '../../types/nosto'
import { useAmplitude } from '../_components/chrome/scripts/AmplitudeScript'
import { useNostoClient } from '../_components/chrome/scripts/NostoScript'
import { ConsentPolicy } from '../_config/ConsentPolicy.config'
import { cookieKeys } from '../_config/Cookies.config'
import { isDefinedAndNotNull } from '../_utils/isDefinedAndNotNull'
import { shortenShopifyId } from '../_utils/shortenShopifyId'

import { useAuthentication } from './AuthenticationProvider.client'
import { useConsentPolicy } from './ConsentPolicyProvider.client'
import { useCookies } from './CookiesProvider.client'
import { useLocalization } from './LocalizationProvider.client'
import { useSession } from './SessionProvider.client'

import type {
  IterableRequestBody,
  IterableTrackCartLineEventPayload,
  IterableTrackCollectionViewPayload,
  IterableTrackPdpViewPayload,
} from '../../api/dynamic/iterableRequest'
import type { PageType } from '../_hooks/useStaticTracking'

type RequestContext = {
  __TypeInfo: 'request'
  pageUrl: string
  pageTitle: string
  timezone: string
  userAgent: string
}

type UserContext = {
  __TypeInfo: 'user'
  emailAddress: string
  accountId: string
}

type ThirdPartyContext = {
  __TypeInfo: 'thirdparty'
  amplitudeSessionId?: number
}

type MarketingContext = {
  __TypeInfo: 'marketing'
  source?: string
  medium?: string
  campaign?: string
  ttCampaignId?: string
  initialPage?: string
  initialReferrer?: string
}

type ConsentContext = {
  __TypeInfo: 'consent'
} & Record<keyof ConsentPolicy, boolean>

type JSONValue = string | number | boolean | null | JSONValue[] | { [key: string]: JSONValue }

type EventToTrack = {
  event: string
  attributes: Record<string, JSONValue>
}

type SignalPayload = EventToTrack & {
  createdAt: string
  context: Array<
    RequestContext | UserContext | ThirdPartyContext | MarketingContext | ConsentContext
  >
}

const signalUrlEndpoint = '/api/user-signal/v1/signals'
const amplitudeCookieId = 'AMP_' + process.env.NEXT_PUBLIC_AMPLITUDE_API_KEY?.slice(0, 10)

const useTrackingImpl = () => {
  const queuedEventBodiesRef = useRef<SignalPayload[]>([])
  const { account } = useAuthentication()
  const { consentPolicy, status: consentLoadingStatus } = useConsentPolicy()
  const [cookies] = useCookies([cookieKeys.authToken.key, amplitudeCookieId])
  const {
    attributes: { source: sessionSource },
  } = useSession()

  const buildConsentContext = () => {
    const consentContext: ConsentContext = {
      __TypeInfo: 'consent',
      essential: consentPolicy.essential === 'allowed',
      performance: consentPolicy.performance === 'allowed',
      functional: consentPolicy.functional === 'allowed',
      targeting: consentPolicy.targeting === 'allowed',
      social: consentPolicy.social === 'allowed',
    }
    return consentContext
  }

  const buildRequestContext = () => {
    const requestContext: RequestContext = {
      __TypeInfo: 'request',
      pageTitle: document.title,
      pageUrl: document.URL,
      // TODO: Fix this auto-generated suppression.
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      timezone: Intl?.DateTimeFormat().resolvedOptions().timeZone,
      userAgent: window.navigator.userAgent,
    }
    return requestContext
  }

  const getMarketingContext = (): MarketingContext | null => {
    if (sessionSource) {
      try {
        return {
          __TypeInfo: 'marketing',
          ...sessionSource,
        }
      } catch {}
    }

    // This fallback should never occur since we set session source in CloudFront functions
    return null
  }

  const buildUserContext = ({ user }: { user: Omit<UserContext, '__TypeInfo'> }) => {
    const userContext: UserContext = {
      __TypeInfo: 'user',
      emailAddress: user.emailAddress,
      accountId: user.accountId,
    }
    return userContext
  }

  const buildThirdPartyContext = () => {
    let amplitudeCookieSessionId: number | null = null
    try {
      const parsedAmplitudeCookie = JSON.parse(
        decodeURIComponent(atob(cookies[amplitudeCookieId] ?? '{}'))
      )
      if (typeof parsedAmplitudeCookie.sessionId === 'number') {
        amplitudeCookieSessionId = parsedAmplitudeCookie.sessionId
      }
    } catch {}

    // Fallback to Amplitude cookie in case the signal send happens before Amplitude finishes loading.
    const amplitudeSessionId = globalThis.amplitude?.getSessionId?.() ?? amplitudeCookieSessionId

    if (amplitudeSessionId !== null) {
      const thirdpartyContext: ThirdPartyContext = {
        __TypeInfo: 'thirdparty',
        ...{ amplitudeSessionId },
      }
      return thirdpartyContext
    }
    return null
  }

  // Context relevant to the moment the event happened
  const createTimeOfEventContexts = ({
    user,
  }: {
    user: Omit<UserContext, '__TypeInfo'> | undefined | null
  }) => {
    const context = []

    if (user) {
      context.push(buildUserContext({ user }))
    }

    context.push(buildRequestContext())

    const marketingContext = getMarketingContext()
    if (marketingContext) {
      context.push(marketingContext)
    }

    return context
  }

  // Contexts that need to be fresh at send
  const createTimeOfSendContexts = () => {
    const context = []

    context.push(buildConsentContext())

    const thirdpartyContext = buildThirdPartyContext()
    if (thirdpartyContext) {
      context.push(thirdpartyContext)
    }

    return context
  }

  const postSignalBody = useStableCallback((body: SignalPayload) => {
    body.context.push(...createTimeOfSendContexts())

    const headers = new Headers({
      'content-type': 'application/json',
    })

    const authorization = cookies[cookieKeys.authToken.key]
    if (authorization) {
      headers.append('Authorization', authorization)
    }

    fetch(signalUrlEndpoint, {
      method: 'POST',
      headers: headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(_error => {})
  })

  const sendSignal = useStableCallback(({ event, attributes }: EventToTrack) => {
    if (process.env.NEXT_PUBLIC_USER_SIGNALS_ENABLED !== 'true') return

    const data: SignalPayload = {
      event,
      attributes,
      context: createTimeOfEventContexts({
        user: account
          ? {
              emailAddress: account.email,
              accountId: account.id,
            }
          : undefined,
      }),
      createdAt: new Date().toISOString(),
    }

    if (consentPolicy.performance === 'allowed') {
      postSignalBody(data)
    } else if (consentLoadingStatus === 'pending') {
      queuedEventBodiesRef.current.push(data)
    }
  })

  useEffect(() => {
    if (consentPolicy.performance === 'allowed' && queuedEventBodiesRef.current.length > 0) {
      queuedEventBodiesRef.current.forEach(queuedRequestBody => {
        postSignalBody(queuedRequestBody)
      })
      queuedEventBodiesRef.current = []
    }
  }, [consentPolicy.performance, postSignalBody])

  return {
    sendSignal,
  }
}

const TrackingContainer = createContainer(useTrackingImpl)
TrackingContainer.Provider.displayName = 'TrackingProviderImpl'
export const TrackingProvider = ({ children }: { children?: ReactNode }) => {
  const { track: amplitudeTrack } = useAmplitude()
  const track = useCallback<LmfAnalyticsTrackFunction>(
    ({ action, element, context, label, variant, href }) => {
      return amplitudeTrack({
        eventId: undefined,
        eventName: action,
        eventProperties: {
          target_label: label,
          target_element: element,
          target_variant: variant,
          target_href: href,
          document_context: context,
        },
      })
    },
    [amplitudeTrack]
  )
  return (
    <TrackingContainer.Provider>
      <LmfAnalyticsProvider title={'Document'} track={track}>
        {children}
      </LmfAnalyticsProvider>
    </TrackingContainer.Provider>
  )
}

type UseTrackFeatureFlagDecisionInput = {
  key: string
  decision: string
}

export const useTrackFeatureFlagDecision = () => {
  const { sendFeatureFlagDecision } = useAmplitude()

  const trackFeatureFlagDecision = useCallback(
    (featureFlagDecision: UseTrackFeatureFlagDecisionInput) => {
      sendFeatureFlagDecision(featureFlagDecision.key, featureFlagDecision.decision)
    },
    [sendFeatureFlagDecision]
  )

  return {
    trackFeatureFlagDecision: useStableCallback(trackFeatureFlagDecision),
  }
}

const withoutUndefined = (record: Record<string, JSONValue | undefined>) =>
  Object.fromEntries(
    Object.entries(record).filter((entry): entry is [string, JSONValue] => entry[1] !== undefined)
  )

// Signal twin of the `Critical Element Selected` Amplitude event, so it also reaches Segment.
// `forward: 'segment'` tells the Segment adapter to send the signal on verbatim as a Track call.
export const useTrackCriticalElementSelected = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackCriticalElementSelected = useCallback(
    (properties: Record<string, JSONValue | undefined>) => {
      sendSignal({
        event: 'critical_element_selected',
        attributes: {
          ...withoutUndefined(properties),
          forward: 'segment',
        },
      })
    },
    [sendSignal]
  )

  return {
    trackCriticalElementSelected: useStableCallback(trackCriticalElementSelected),
  }
}

// Where on the page a signal's interaction happened. See `TrackingLocationProvider`.
// snake_case to match the `Critical Element Selected` properties, so Amplitude shares one property per field.
export type SignalTrackingLocation = {
  location: PageType | null
  secondary_location: string | null
  section_number: number | null
  position: number | null
  href: string | null
  // Add to bag only: whether it came from a Quickbuy dialog.
  quickbuy?: boolean
}

type UseTrackProductInteractionInput = {
  handle: string
  color: string | undefined | null
  id: string
  shopifyId: string
  soldOut: boolean
  tracking?: SignalTrackingLocation
}

export const useTrackProductInteraction = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackProductInteraction = useCallback(
    (productSignal: UseTrackProductInteractionInput) => {
      sendSignal({
        event: 'product_interaction',
        attributes: {
          id: productSignal.id,
          handle: productSignal.handle,
          color: productSignal.color ?? null,
          shopifyId: shortenShopifyId(productSignal.shopifyId),
          soldOut: productSignal.soldOut,
          // Same `items.handle` the cart signals send, for funnels held constant on it.
          items: [{ handle: productSignal.handle }],
          ...productSignal.tracking,
        },
      })
    },
    [sendSignal]
  )

  return {
    trackProductInteraction: useStableCallback(trackProductInteraction),
  }
}

type useTrackProductFavoritedInput = {
  handle: string
  color: string | undefined | null
  id: string
  shopifyId: string
}

export const useTrackProductFavorited = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackProductFavorited = useCallback(
    (signal: useTrackProductFavoritedInput) => {
      sendSignal({
        event: 'product_favorited',
        attributes: {
          id: signal.id,
          handle: signal.handle,
          color: signal.color ?? null,
          shopifyId: shortenShopifyId(signal.shopifyId),
        },
      })
    },
    [sendSignal]
  )

  return {
    trackProductFavorited: useStableCallback(trackProductFavorited),
  }
}

type UseTrackJoinedProductWaitlistInput = {
  handle: string
  id: string
  email: string
  productShopifyId: string
  variantShopifyId: string
  variantSku: string
  colorId: string | undefined | null
  sizeId: string | undefined | null
  fitId: string | undefined | null
}

export const useTrackJoinedProductWaitlist = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackJoinedProductWaitlist = useCallback(
    (signal: UseTrackJoinedProductWaitlistInput) => {
      sendSignal({
        event: 'joined_product_waitlist',
        attributes: {
          email: signal.email,
          id: signal.id,
          handle: signal.handle,
          productShopifyId: shortenShopifyId(signal.productShopifyId),
          variantShopifyId: shortenShopifyId(signal.variantShopifyId),
          variantSku: signal.variantSku,
          colorId: signal.colorId ?? null,
          sizeId: signal.sizeId ?? null,
          fitId: signal.fitId ?? null,
        },
      })
    },
    [sendSignal]
  )

  return {
    trackJoinedProductWaitlist: useStableCallback(trackJoinedProductWaitlist),
  }
}

type UseTrackProductReviewedInput = {
  handle: string
  color: string | undefined | null
  id: string
  email: string
  rating: number
  shopifyId: string
}

export const useTrackProductReviewed = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackProductReviewed = useCallback(
    (signal: UseTrackProductReviewedInput) => {
      sendSignal({
        event: 'product_reviewed',
        attributes: {
          id: signal.id,
          handle: signal.handle,
          color: signal.color ?? null,
          shopifyId: shortenShopifyId(signal.shopifyId),
          email: signal.email,
          rating: signal.rating,
        },
      })
    },
    [sendSignal]
  )

  return {
    trackProductReviewed: useStableCallback(trackProductReviewed),
  }
}

type UseTrackProductImpressionInput = {
  featuredProduct: boolean
  handle: string
  id: string
  shopifyId: string
  soldOut: boolean
  color: string | undefined | null
}

export const useTrackProductImpression = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackProductImpression = useCallback(
    (productImpression: UseTrackProductImpressionInput) => {
      sendSignal({
        event: 'product_impression',
        attributes: {
          id: productImpression.id,
          featuredProduct: productImpression.featuredProduct,
          handle: productImpression.handle,
          color: productImpression.color ?? null,
          soldOut: productImpression.soldOut,
          shopifyId: shortenShopifyId(productImpression.shopifyId),
          variantInformation: [], // Delete this if possible. Adapter services can fetch it instead.
        },
      })
    },
    [sendSignal]
  )

  return {
    trackProductImpression: useStableCallback(trackProductImpression),
  }
}

type UseTrackCollectionFilteredInput = {
  collectionHandle: string
  filters: Array<{
    facet: string
    values: Array<string>
  }>
}

export const useTrackCollectionFiltered = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackCollectionFiltered = useCallback(
    (signal: UseTrackCollectionFilteredInput) => {
      sendSignal({
        event: 'collection_filtered',
        attributes: {
          collectionHandle: signal.collectionHandle,
          filters: signal.filters,
        },
      })
    },
    [sendSignal]
  )

  return {
    trackCollectionFiltered: useStableCallback(trackCollectionFiltered),
  }
}

type PageViewCategory =
  | 'account'
  | 'home'
  | 'landing'
  | 'favorites'
  | 'collection'
  | 'kit'
  | 'product'
  | 'search'
  | 'not found'
  | 'other'

const signalApiPageViewCategories: Record<PageViewCategory, string> = {
  'account': 'other',
  'collection': 'collection',
  'favorites': 'other',
  'home': 'home',
  'kit': 'collection',
  'landing': 'landing',
  'not found': 'not found',
  'other': 'other',
  'product': 'product',
  'search': 'search',
} as const

const globalValidNostoPlacements = [
  'cartpage-nosto-1',
  'cartpage-nosto-2',
  'cartpage-nosto-3',
  'cartpage-nosto-4',
  'favorites-nosto-1',
  'productcategory-nosto-1',
] as const

const nostoPlacementsForPageCategories: Record<PageViewCategory, NostoPlacementId[]> = {
  'account': [...globalValidNostoPlacements],
  'collection': [...globalValidNostoPlacements],
  'home': [
    'frontpage-nosto-1',
    'frontpage-nosto-2',
    'frontpage-nosto-3',
    'frontpage-nosto-4',
    'frontpage-nosto-5',
    'frontpage-nosto-6',
    'frontpage-nosto-7',
    'frontpage-nosto-8',
    'frontpage-nosto-9',
    'frontpage-nosto-10',
    'frontpage-nosto-11',
    'frontpage-nosto-12',
    ...globalValidNostoPlacements,
  ],
  'kit': [
    'productpage-nosto-4',
    'productpage-nosto-8',
    'productpage-nosto-9',
    'productpage-nosto-10',
    ...globalValidNostoPlacements,
  ],
  'landing': [
    'frontpage-nosto-1',
    'frontpage-nosto-2',
    'frontpage-nosto-3',
    'frontpage-nosto-4',
    'frontpage-nosto-5',
    'frontpage-nosto-6',
    'frontpage-nosto-7',
    'frontpage-nosto-8',
    'frontpage-nosto-9',
    'frontpage-nosto-10',
    'frontpage-nosto-11',
    'frontpage-nosto-12',
    ...globalValidNostoPlacements,
  ],
  'favorites': [...globalValidNostoPlacements],
  'not found': ['notfound-nosto-1', 'notfound-nosto-2', ...globalValidNostoPlacements],
  'other': [...globalValidNostoPlacements],
  'product': [
    'productpage-nosto-1',
    'productpage-cts-nosto-1',
    'productpage-wiw-nosto-1',
    'productpage-nosto-3',
    ...globalValidNostoPlacements,
  ],
  'search': ['searchpage-nosto-1', 'searchpage-nosto-2', ...globalValidNostoPlacements],
} as const

type IterablePageView =
  | {
      type: 'trackCollectionView'
      payload: Omit<IterableTrackCollectionViewPayload, 'view'>
    }
  | {
      type: 'trackPdpView'
      payload: Omit<IterableTrackPdpViewPayload, 'view'>
    }

const postIterableEvent = async (requestBody: IterableRequestBody) => {
  const iterableApiPath =
    process.env.NEXT_PUBLIC_FIGS_ENV === 'local'
      ? '/api/dynamic/iterable'
      : '/api/magnolia/iterable'

  await fetch(iterableApiPath, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    keepalive: true,
    body: JSON.stringify(requestBody),
  })
}

const buildIterablePageViewRequestBody = ({
  iterable,
  path,
}: {
  iterable: IterablePageView
  path: string
}): IterableRequestBody => {
  switch (iterable.type) {
    case 'trackCollectionView':
      return {
        type: iterable.type,
        payload: {
          ...iterable.payload,
          view: {
            path,
          },
        },
      }
    case 'trackPdpView':
      return {
        type: iterable.type,
        payload: {
          ...iterable.payload,
          view: {
            path,
          },
        },
      }
  }
}

type CollectionPageView = {
  pageViewHandle: string
  pageViewName: string
  pageViewCategory: Extract<PageViewCategory, 'collection'>
  productShopifyId?: never
  searchQuery?: never
  iterable?: Extract<IterablePageView, { type: 'trackCollectionView' }>
}

type ProductPageView = {
  pageViewHandle: string
  pageViewName: string
  pageViewCategory: Extract<PageViewCategory, 'product'>
  productShopifyId: string
  searchQuery?: never
  iterable?: Extract<IterablePageView, { type: 'trackPdpView' }>
}

type SearchPageView = {
  pageViewHandle: string
  pageViewName: string
  pageViewCategory: Extract<PageViewCategory, 'search'>
  productShopifyId?: never
  searchQuery: string
  iterable?: never
}

type GenericPageView = {
  pageViewHandle: string
  pageViewName: string
  pageViewCategory: Exclude<PageViewCategory, 'collection' | 'product' | 'search'>
  productShopifyId?: never
  searchQuery?: never
  iterable?: never
}

type PageView = CollectionPageView | ProductPageView | SearchPageView | GenericPageView

export const useTrackPageView = () => {
  const nostoClient = useNostoClient()
  const { sendSignal } = TrackingContainer.useContainer()
  const lastIterableRequestBodyRef = useRef<string | undefined>(undefined)

  const trackPageView = useCallback(
    ({
      pageViewHandle,
      pageViewName,
      pageViewCategory,
      productShopifyId,
      searchQuery,
      iterable,
    }: PageView) => {
      // Nosto
      const nostoPlacementIds = nostoPlacementsForPageCategories[pageViewCategory]
      if (pageViewCategory === 'home') {
        nostoClient.trackHomePageView(nostoPlacementIds)
      } else if (pageViewCategory === 'collection') {
        nostoClient.trackCollectionPageView({
          collectionHandle: pageViewName, // Its odd we send name to nosto as handle. Maybe investigate some day.
          nostoPlacementIds,
        })
      } else if (pageViewCategory === 'kit') {
        nostoClient.trackCollectionPageView({
          collectionHandle: pageViewName, // Its odd we send name to nosto as handle. Maybe investigate some day.
          nostoPlacementIds,
        })
      } else if (pageViewCategory === 'not found') {
        nostoClient.trackNotFoundPageView(nostoPlacementIds)
      } else if (pageViewCategory === 'account') {
        nostoClient.trackGeneralPageView(nostoPlacementIds)
      } else if (pageViewCategory === 'product') {
        nostoClient.trackProductPageView({
          shopifyProductGid: productShopifyId,
          nostoPlacementIds,
        })
      } else if (pageViewCategory === 'search') {
        nostoClient.clearRecommendations(nostoPlacementIds)
        nostoClient.trackSearchEvent(searchQuery, nostoPlacementIds)
      } else if (pageViewCategory === 'landing') {
        nostoClient.trackGeneralPageView(nostoPlacementIds)
      } else if (pageViewCategory === 'favorites') {
        nostoClient.trackGeneralPageView(nostoPlacementIds)
      } else {
        nostoClient.trackGeneralPageView()
      }

      // Signal
      sendSignal({
        event: 'page_view',
        attributes: {
          pageHandle: pageViewHandle,
          pageName: pageViewName,
          pageCategory: signalApiPageViewCategories[pageViewCategory],
        },
      })

      // Iterable
      if (iterable) {
        const iterableRequestBody = buildIterablePageViewRequestBody({
          iterable,
          path: window.location.pathname,
        })
        const serializedRequestBody = JSON.stringify(iterableRequestBody)

        if (lastIterableRequestBodyRef.current === serializedRequestBody) return
        lastIterableRequestBodyRef.current = serializedRequestBody

        postIterableEvent(iterableRequestBody).catch(() => undefined)
      }
    },
    [nostoClient, sendSignal]
  )

  return {
    trackPageView: useStableCallback(trackPageView),
  }
}

// This only does generic page views since they are static data.
// Let more complex situations be handled by the consumer with useTrackPageView directly
export const TrackPageView = (pageView: GenericPageView) => {
  const { trackPageView } = useTrackPageView()
  const priorPageViewKeyRef = useRef('')
  useEffect(() => {
    const pageViewKey = `${pageView.pageViewCategory}:${pageView.pageViewHandle}`
    // TODO: Fix this auto-generated suppression.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (priorPageViewKeyRef.current === pageViewKey) return
    priorPageViewKeyRef.current = pageViewKey
    trackPageView(pageView)
  }, [pageView, trackPageView])

  return null
}

export type TrackedCartItem = {
  effectivePrice: number
  quantity: number
  productShopifyId: string
  variantShopifyId: string
  variantSku: string
  variantSoldOut: boolean
  currencyCode: string
  handle: string
  categoryId: string
  colorId: string | undefined | null
  sizeId: string | undefined | null
  fitId: string | undefined | null
  gender?: string
  imageUrl?: string
  availableForSale?: boolean
  title?: string
}

type UseTrackCartModifiedInput = {
  id: string
  items: Array<
    TrackedCartItem & {
      title: string
    }
  >
}

const postIterableCartLineEvent = (
  payload: Omit<IterableTrackCartLineEventPayload, 'item'> & { item: TrackedCartItem }
) => {
  void postIterableEvent({
    type: 'trackCartLineEvent',
    payload: {
      eventName: payload.eventName,
      cartId: payload.cartId,
      item: toIterableCartLinePayload(payload.item),
      previousQuantity: payload.previousQuantity,
    },
  }).catch(() => undefined)
}

export const useTrackCartModified = () => {
  const nostoClient = useNostoClient()
  const { region } = useLocalization()
  const { sendSignal } = TrackingContainer.useContainer()

  const trackCartModified = useCallback(
    (cartModification: UseTrackCartModifiedInput) => {
      const cartHashKey = '@figs:SignalCartHash'
      const existingCartHashValue = localStorage.getItem(cartHashKey)
      const initializingEmptyCart =
        (existingCartHashValue === null && cartModification.items.length === 0) ||
        (!existingCartHashValue?.startsWith(cartModification.id) &&
          cartModification.items.length === 0)
      const cartHashValue =
        cartModification.id +
        '||' +
        cartModification.items.map(item => item.variantSku + '|' + item.quantity).join('||')

      if (!initializingEmptyCart && cartHashValue !== existingCartHashValue) {
        localStorage.setItem(cartHashKey, cartHashValue)

        sendSignal({
          event: 'cart_modified',
          attributes: {
            id: cartModification.id,
            items: cartModification.items.map(cartItem => ({
              effectivePrice: cartItem.effectivePrice,
              currencyCode: cartItem.currencyCode,
              quantity: cartItem.quantity,
              productShopifyId: shortenShopifyId(cartItem.productShopifyId),
              variantShopifyId: shortenShopifyId(cartItem.variantShopifyId),
              variantSku: cartItem.variantSku,
              variantSoldOut: cartItem.variantSoldOut,
              handle: cartItem.handle,
              colorId: cartItem.colorId ?? null,
              sizeId: cartItem.sizeId ?? null,
              fitId: cartItem.fitId ?? null,
            })),
          },
        })

        void postIterableEvent({
          type: 'trackCartItems',
          payload: {
            items: cartModification.items.map(item => toIterableCartLinePayload(item)),
          },
        }).catch(() => undefined)

        const cartChangeProps: Parameters<typeof nostoClient.trackCartChange>[0] = {
          currencyCode: region.currency,
          cartItems: Object.values(cartModification.items).map(item => {
            return {
              title: item.title,
              quantity: item.quantity,
              effectivePriceInCents: item.effectivePrice,
              shopifyProductGid: item.productShopifyId,
              shopifyVariantGid: item.variantShopifyId,
            }
          }),
          nostoPlacementIds: globalValidNostoPlacements,
        }
        nostoClient.trackCartChange(cartChangeProps)
      }
    },
    [sendSignal, region.currency, nostoClient]
  )

  return {
    trackCartModified: useStableCallback(trackCartModified),
  }
}

// One cart line in signal shape. Every cart signal sends `items` so `items.handle` can be held
// constant from product interaction through checkout.
const toSignalCartLineItem = (cartItem: TrackedCartItem) => ({
  effectivePrice: cartItem.effectivePrice,
  currencyCode: cartItem.currencyCode,
  quantity: cartItem.quantity,
  productShopifyId: shortenShopifyId(cartItem.productShopifyId),
  variantShopifyId: shortenShopifyId(cartItem.variantShopifyId),
  variantSku: cartItem.variantSku,
  variantSoldOut: cartItem.variantSoldOut,
  handle: cartItem.handle,
  colorId: cartItem.colorId ?? null,
  sizeId: cartItem.sizeId ?? null,
  fitId: cartItem.fitId ?? null,
})

type UseTrackCartItemAddedInput = {
  cartId: string | undefined | null
  item: TrackedCartItem
  tracking?: SignalTrackingLocation
}

export const useTrackCartItemAdded = () => {
  const { sendSignal } = TrackingContainer.useContainer()
  const { pushAddToCart: pushAddToCartToGtm } = useGoogleTagManager()

  const trackCartItemAdded = useCallback(
    (cartItemAddedEvent: UseTrackCartItemAddedInput) => {
      pushAddToCartToGtm({
        quantity: cartItemAddedEvent.item.quantity,
        variant: {
          shopifyId: cartItemAddedEvent.item.variantShopifyId,
          currencyType: cartItemAddedEvent.item.currencyCode,
          price: cartItemAddedEvent.item.effectivePrice,
          sku: cartItemAddedEvent.item.variantSku,
          externalParentId: cartItemAddedEvent.item.productShopifyId,
          discountPrice: null,
          handle: cartItemAddedEvent.item.handle,
          category: cartItemAddedEvent.item.categoryId,
        },
      })
      sendSignal({
        event: 'cart_item_added',
        attributes: {
          cartId: cartItemAddedEvent.cartId ?? null,
          effectivePrice: cartItemAddedEvent.item.effectivePrice,
          currencyCode: cartItemAddedEvent.item.currencyCode,
          quantity: cartItemAddedEvent.item.quantity,
          productShopifyId: shortenShopifyId(cartItemAddedEvent.item.productShopifyId),
          variantShopifyId: shortenShopifyId(cartItemAddedEvent.item.variantShopifyId),
          variantSku: cartItemAddedEvent.item.variantSku,
          variantSoldOut: cartItemAddedEvent.item.variantSoldOut,
          handle: cartItemAddedEvent.item.handle,
          colorId: cartItemAddedEvent.item.colorId ?? null,
          sizeId: cartItemAddedEvent.item.sizeId ?? null,
          fitId: cartItemAddedEvent.item.fitId ?? null,
          items: [toSignalCartLineItem(cartItemAddedEvent.item)],
          ...cartItemAddedEvent.tracking,
        },
      })

      postIterableCartLineEvent({
        eventName: iterableCartItemAddedEventName,
        cartId: cartItemAddedEvent.cartId ?? null,
        item: cartItemAddedEvent.item,
      })
    },
    [pushAddToCartToGtm, sendSignal]
  )

  return {
    trackCartItemAdded: useStableCallback(trackCartItemAdded),
  }
}

type UseTrackCartItemRemovedInput = {
  cartId: string | undefined | null
  item: TrackedCartItem
}

export const useTrackCartItemRemoved = () => {
  const { sendSignal } = TrackingContainer.useContainer()
  const { pushRemoveFromCart: pushRemoveFromCartToGtm } = useGoogleTagManager()

  const trackCartItemRemoved = useCallback(
    (cartItemRemovedEvent: UseTrackCartItemRemovedInput) => {
      pushRemoveFromCartToGtm({
        quantity: cartItemRemovedEvent.item.quantity,
        variant: {
          shopifyId: cartItemRemovedEvent.item.variantShopifyId,
          currencyType: cartItemRemovedEvent.item.currencyCode,
          price: cartItemRemovedEvent.item.effectivePrice,
          sku: cartItemRemovedEvent.item.variantSku,
          externalParentId: cartItemRemovedEvent.item.productShopifyId,
          discountPrice: null,
          handle: cartItemRemovedEvent.item.handle,
          category: cartItemRemovedEvent.item.categoryId,
        },
      })
      sendSignal({
        event: 'cart_item_removed',
        attributes: {
          cartId: cartItemRemovedEvent.cartId ?? null,
          effectivePrice: cartItemRemovedEvent.item.effectivePrice,
          currencyCode: cartItemRemovedEvent.item.currencyCode,
          quantity: cartItemRemovedEvent.item.quantity,
          productShopifyId: shortenShopifyId(cartItemRemovedEvent.item.productShopifyId),
          variantShopifyId: shortenShopifyId(cartItemRemovedEvent.item.variantShopifyId),
          variantSku: cartItemRemovedEvent.item.variantSku,
          variantSoldOut: cartItemRemovedEvent.item.variantSoldOut,
          handle: cartItemRemovedEvent.item.handle,
          colorId: cartItemRemovedEvent.item.colorId ?? null,
          sizeId: cartItemRemovedEvent.item.sizeId ?? null,
          fitId: cartItemRemovedEvent.item.fitId ?? null,
          items: [toSignalCartLineItem(cartItemRemovedEvent.item)],
        },
      })

      postIterableCartLineEvent({
        eventName: iterableCartItemRemovedEventName,
        cartId: cartItemRemovedEvent.cartId ?? null,
        item: cartItemRemovedEvent.item,
      })
    },
    [pushRemoveFromCartToGtm, sendSignal]
  )

  return {
    trackCartItemRemoved: useStableCallback(trackCartItemRemoved),
  }
}

type UseTrackCartItemUpdatedInput = {
  cartId: string | undefined | null
  item: TrackedCartItem & {
    previousQuantity: number
  }
  // Only set when the update came from an add to bag of an item already in the cart.
  tracking?: SignalTrackingLocation
}

export const useTrackCartItemUpdated = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackCartItemUpdated = useCallback(
    (cartItemUpdatedEvent: UseTrackCartItemUpdatedInput) => {
      const { previousQuantity, quantity } = cartItemUpdatedEvent.item

      sendSignal({
        event: 'cart_item_updated',
        attributes: {
          cartId: cartItemUpdatedEvent.cartId ?? null,
          effectivePrice: cartItemUpdatedEvent.item.effectivePrice,
          currencyCode: cartItemUpdatedEvent.item.currencyCode,
          previousQuantity,
          quantity,
          productShopifyId: shortenShopifyId(cartItemUpdatedEvent.item.productShopifyId),
          variantShopifyId: shortenShopifyId(cartItemUpdatedEvent.item.variantShopifyId),
          variantSku: cartItemUpdatedEvent.item.variantSku,
          variantSoldOut: cartItemUpdatedEvent.item.variantSoldOut,
          handle: cartItemUpdatedEvent.item.handle,
          colorId: cartItemUpdatedEvent.item.colorId ?? null,
          sizeId: cartItemUpdatedEvent.item.sizeId ?? null,
          fitId: cartItemUpdatedEvent.item.fitId ?? null,
          items: [toSignalCartLineItem(cartItemUpdatedEvent.item)],
          ...cartItemUpdatedEvent.tracking,
        },
      })

      if (quantity > previousQuantity) {
        postIterableCartLineEvent({
          eventName: iterableCartItemAddedEventName,
          cartId: cartItemUpdatedEvent.cartId ?? null,
          item: cartItemUpdatedEvent.item,
          previousQuantity,
        })
      } else if (quantity < previousQuantity) {
        postIterableCartLineEvent({
          eventName: iterableCartItemRemovedEventName,
          cartId: cartItemUpdatedEvent.cartId ?? null,
          item: cartItemUpdatedEvent.item,
          previousQuantity,
        })
      }
    },
    [sendSignal]
  )

  return {
    trackCartItemUpdated: useStableCallback(trackCartItemUpdated),
  }
}

type UseTrackCartOpenedInput = {
  cartId: string | null
  cart: Omit<Cart, 'id'>
}

export const useTrackCartOpened = () => {
  const { sendSignal } = TrackingContainer.useContainer()
  const convertCartItemIntoSignalData = useConvertCartItemIntoSignalData()

  const trackCartOpened = useCallback(
    (cartOpened: UseTrackCartOpenedInput) => {
      sendSignal({
        event: 'cart_opened',
        attributes: {
          cartId: cartOpened.cartId,
          // In cents, like each item's effectivePrice.
          cartValue: cartOpened.cart.totalPrice,
          items: Object.values(cartOpened.cart.items)
            .map(convertCartItemIntoSignalData)
            .filter(isDefinedAndNotNull)
            .map(toSignalCartLineItem),
        },
      })
    },
    [convertCartItemIntoSignalData, sendSignal]
  )

  return {
    trackCartOpened: useStableCallback(trackCartOpened),
  }
}

type UseTrackProceedToCheckoutInput = {
  cart: Omit<Cart, 'id'>
  shopifyCartId: string | null
  shopifyCheckoutURL: string
  currencyCode: string
}

export const useTrackProceedToCheckout = () => {
  const { sendSignal } = TrackingContainer.useContainer()
  const convertCartItemIntoSignalData = useConvertCartItemIntoSignalData()
  const { pushProceedToCheckout: pushProceedToCheckoutToGtm } = useGoogleTagManager()

  const trackProceedToCheckout = useCallback(
    (proceedToCheckout: UseTrackProceedToCheckoutInput) => {
      const signalCheckoutItems = Object.values(proceedToCheckout.cart.items)
        .map(convertCartItemIntoSignalData)
        .filter(isDefinedAndNotNull)

      sendSignal({
        event: 'proceed_to_checkout',
        attributes: {
          shopifyCartId: proceedToCheckout.shopifyCartId,
          shopifyCheckoutURL: proceedToCheckout.shopifyCheckoutURL,
          // In cents, like each item's effectivePrice.
          cartValue: proceedToCheckout.cart.totalPrice,
          items: signalCheckoutItems.map(toSignalCartLineItem),
        },
      })
      pushProceedToCheckoutToGtm(proceedToCheckout.cart, proceedToCheckout.currencyCode)
    },
    [convertCartItemIntoSignalData, pushProceedToCheckoutToGtm, sendSignal]
  )

  return {
    trackProceedToCheckout: useStableCallback(trackProceedToCheckout),
  }
}

type UseTrackProceedToReturnsPortalInput = {
  loopCartToken: string
  loopCheckoutURL: string
  items: Array<TrackedCartItem>
}

export const useTrackProceedToReturnsPortal = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackProceedToReturnsPortal = useCallback(
    (proceedToReturnsPortal: UseTrackProceedToReturnsPortalInput) => {
      sendSignal({
        event: 'proceed_to_returns_portal',
        attributes: {
          loopCartToken: proceedToReturnsPortal.loopCartToken,
          loopCheckoutURL: proceedToReturnsPortal.loopCheckoutURL,
          items: proceedToReturnsPortal.items.map(cartItem => ({
            effectivePrice: cartItem.effectivePrice,
            currencyCode: cartItem.currencyCode,
            quantity: cartItem.quantity,
            productShopifyId: shortenShopifyId(cartItem.productShopifyId),
            variantShopifyId: shortenShopifyId(cartItem.variantShopifyId),
            variantSku: cartItem.variantSku,
            variantSoldOut: cartItem.variantSoldOut,
            handle: cartItem.handle,
            colorId: cartItem.colorId ?? null,
            sizeId: cartItem.sizeId ?? null,
            fitId: cartItem.fitId ?? null,
          })),
        },
      })
    },
    [sendSignal]
  )

  return {
    trackProceedToReturnsPortal: useStableCallback(trackProceedToReturnsPortal),
  }
}

type UseTrackSearchResultInput = {
  id: string
  query: string
  searchType: 'header' | 'page'
  topResults: Array<{
    handle: string
    color: string | null | undefined
    id: string
    shopifyId: string
    soldOut: boolean
  }>
  totalResults: number
}

export const useTrackTeamsAccountCreation = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackTeamsAccountCreation = useCallback(
    ({ email, emailOptIn }: { email: string; emailOptIn: boolean }) => {
      sendSignal({
        event: 'teams_onboarding_account_creation',
        attributes: {
          email,
          opt_in_email: emailOptIn,
          funnel_step: '2',
        },
      })
    },
    [sendSignal]
  )

  return {
    trackTeamsAccountCreation: useStableCallback(trackTeamsAccountCreation),
  }
}

export const useTrackSearchResults = () => {
  const { sendSignal } = TrackingContainer.useContainer()

  const trackSearchResults = useCallback(
    (searchResults: UseTrackSearchResultInput) => {
      const topResults = searchResults.topResults.slice(
        0,
        Math.min(10, searchResults.topResults.length)
      )

      sendSignal({
        event: 'search',
        attributes: {
          query: searchResults.query,
          searchType: searchResults.searchType,
          totalResults: searchResults.totalResults,
          topResults: topResults.map(topResult => {
            return {
              ...topResult,
              color: topResult.color ?? null,
              shopifyId: shortenShopifyId(topResult.shopifyId),
            }
          }),
        },
      })
    },
    [sendSignal]
  )

  return {
    trackSearchResults: useStableCallback(trackSearchResults),
  }
}
